// Gestione amministrativa dei consensi (server-consent).
// Due sezioni: "Definizioni" (catalogo + versioning dei testi) e
// "Soggetti" (lookup di una party, stato consensi, revoca, event log).

import { useEffect, useMemo, useState, Fragment, type FormEvent, type ReactNode } from "react";
import axios from "axios";
import {
  createDefinition,
  createDefinitionVersion,
  updateDefinition,
  deleteDefinition,
  fetchDefinitions,
  fetchPartyEvents,
  lookupParty,
  revokeConsent,
  type ConsentDefinition,
  type ConsentDefinitionVersion,
  type ConsentEvent,
  type ConsentKind,
  type PartyConsent,
  type PartyIdentifierType,
  type PartyLookupResult,
} from "../api/consent";

type ToastKind = "success" | "error";
type Notify = (kind: ToastKind, text: string) => void;

const IDENTIFIER_TYPES: { value: PartyIdentifierType; label: string }[] = [
  { value: "sso_oid", label: "SSO OID" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Telefono" },
  { value: "plate_token", label: "Targa (token)" },
];

// ----- Helpers -------------------------------------------------------------

function errorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { error?: string; message?: string } | undefined;
    return data?.error ?? data?.message ?? err.message;
  }
  return err instanceof Error ? err.message : "Errore imprevisto";
}

// The 401 interceptor already redirects to the SSO; don't surface that
// transient rejection as a user-facing error.
function isSessionExpired(msg: string): boolean {
  return msg === "session_expired";
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" });
}

function currentVersion(def: ConsentDefinition): ConsentDefinitionVersion | null {
  if (!def.versions || def.versions.length === 0) return null;
  return def.versions.reduce((max, v) => (v.version > max.version ? v : max));
}

function kindBadgeClass(kind: ConsentKind): string {
  // consent = accent (brand), acknowledgment = neutro
  return kind === "consent" ? "badge badge--accent" : "badge badge--neutral";
}

// Live preview of how a consent/acknowledgment renders to the subject:
// a checkbox + its text, plus kind/blocking markers.
function ConsentPreview({
  kind,
  blocking,
  body,
}: {
  kind: ConsentKind;
  blocking: boolean;
  body: string;
}) {
  const text = body.trim();
  return (
    <div className="consent-preview" aria-label="Anteprima">
      <div className="consent-preview__head">
        <span className="field__hint">Anteprima</span>
        <span className={kindBadgeClass(kind)}>{kind}</span>
        {blocking ? <span className="badge badge--warn">bloccante</span> : null}
      </div>
      <label className="consent-preview__row">
        <input type="checkbox" disabled />
        <span className={text ? "" : "consent-preview__placeholder"}>
          {text || "Il testo del consenso apparirà qui…"}
          {kind === "acknowledgment" && blocking ? " *" : null}
        </span>
      </label>
    </div>
  );
}

// Inline SVG icons (16px, stroke = currentColor). No icon library dependency.
const svgProps = {
  width: 16,
  height: 16,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};
function IconNewVersion() {
  return (
    <svg {...svgProps}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M12 12v6M9 15h6" />
    </svg>
  );
}
function IconEdit() {
  return (
    <svg {...svgProps}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
    </svg>
  );
}
function IconDelete() {
  return (
    <svg {...svgProps}>
      <path d="M3 6h18" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  );
}

function actionBadgeClass(action: string): string {
  const a = action.toLowerCase();
  if (a.includes("grant") || a.includes("accept") || a.includes("ack")) return "badge badge--success";
  if (a.includes("revok") || a.includes("den") || a.includes("declin")) return "badge badge--error";
  return "badge badge--neutral";
}

// ----- Shared UI -----------------------------------------------------------

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div className="modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal__header">
          <h3>{title}</h3>
          <button type="button" className="modal__close" aria-label="Chiudi" onClick={onClose}>
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function TableSkeleton() {
  return (
    <div>
      <div className="skeleton skeleton--line" />
      <div className="skeleton skeleton--line" style={{ width: "85%" }} />
      <div className="skeleton skeleton--line" style={{ width: "70%" }} />
    </div>
  );
}

// ----- Definizioni ---------------------------------------------------------

function NewDefinitionModal({
  onClose,
  onSaved,
  notify,
}: {
  onClose: () => void;
  onSaved: () => void;
  notify: Notify;
}) {
  const [code, setCode] = useState("");
  const [kind, setKind] = useState<ConsentKind>("consent");
  const [legalBasis, setLegalBasis] = useState("");
  const [blocking, setBlocking] = useState(false);
  const [contexts, setContexts] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await createDefinition({
        code: code.trim(),
        kind,
        legal_basis: legalBasis.trim(),
        blocking,
        contexts: contexts
          .split(",")
          .map((c) => c.trim())
          .filter(Boolean),
        body: body.trim(),
      });
      notify("success", `Definizione "${code.trim()}" creata.`);
      onSaved();
    } catch (err) {
      const msg = errorMessage(err);
      if (!isSessionExpired(msg)) notify("error", `Creazione fallita: ${msg}`);
      setBusy(false);
    }
  };

  return (
    <Modal title="Nuova definizione" onClose={onClose}>
      <form className="form-grid" onSubmit={(e) => void submit(e)}>
        <div className="field">
          <label className="field__label" htmlFor="def-code">Codice</label>
          <input
            id="def-code"
            className="input"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="es. marketing_email"
            required
          />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="def-kind">Tipo</label>
          <select
            id="def-kind"
            className="select"
            value={kind}
            onChange={(e) => setKind(e.target.value as ConsentKind)}
          >
            <option value="consent">Consenso (consent)</option>
            <option value="acknowledgment">Presa visione (acknowledgment)</option>
          </select>
        </div>
        <div className="field">
          <label className="field__label" htmlFor="def-legal">Base giuridica</label>
          <select
            id="def-legal"
            className="select"
            value={legalBasis}
            onChange={(e) => setLegalBasis(e.target.value)}
            required
          >
            <option value="">— seleziona —</option>
            <option value="6.1.a">6.1.a — consenso (marketing)</option>
            <option value="6.1.b">6.1.b — misure precontrattuali</option>
            <option value="6.1.c">6.1.c — obbligo di legge</option>
          </select>
        </div>
        <div className="field">
          <label className="field__label" htmlFor="def-contexts">Contesti</label>
          <input
            id="def-contexts"
            className="input"
            value={contexts}
            onChange={(e) => setContexts(e.target.value)}
            placeholder="es. onboarding, checkout"
          />
          <span className="field__hint">Valori separati da virgola.</span>
        </div>
        <label className="check">
          <input
            type="checkbox"
            checked={blocking}
            onChange={(e) => setBlocking(e.target.checked)}
          />
          Bloccante (senza risposta l&apos;utente non procede)
        </label>
        <div className="field">
          <label className="field__label" htmlFor="def-body">Testo (versione 1)</label>
          <textarea
            id="def-body"
            className="textarea"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Testo mostrato al soggetto…"
            required
          />
        </div>
        <ConsentPreview kind={kind} blocking={blocking} body={body} />
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={busy}>
            Annulla
          </button>
          <button type="submit" className="btn" disabled={busy}>
            {busy ? "Creazione…" : "Crea definizione"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function NewVersionModal({
  definition,
  onClose,
  onSaved,
  notify,
}: {
  definition: ConsentDefinition;
  onClose: () => void;
  onSaved: () => void;
  notify: Notify;
}) {
  const [body, setBody] = useState("");
  const [requiresReconsent, setRequiresReconsent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await createDefinitionVersion(definition.code, {
        body: body.trim(),
        requires_reconsent: requiresReconsent,
      });
      notify("success", `Nuova versione di "${definition.code}" pubblicata.`);
      onSaved();
    } catch (err) {
      const msg = errorMessage(err);
      if (!isSessionExpired(msg)) notify("error", `Pubblicazione fallita: ${msg}`);
      setBusy(false);
    }
  };

  return (
    <Modal title={`Nuova versione — ${definition.code}`} onClose={onClose}>
      <form className="form-grid" onSubmit={(e) => void submit(e)}>
        <div className="field">
          <label className="field__label" htmlFor="ver-body">Testo</label>
          <textarea
            id="ver-body"
            className="textarea"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Nuovo testo mostrato al soggetto…"
            required
          />
        </div>
        <label className="check">
          <input
            type="checkbox"
            checked={requiresReconsent}
            onChange={(e) => setRequiresReconsent(e.target.checked)}
          />
          Richiede nuovo consenso (requires_reconsent)
        </label>
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={busy}>
            Annulla
          </button>
          <button type="submit" className="btn" disabled={busy}>
            {busy ? "Pubblicazione…" : "Pubblica versione"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// Edit a definition's mutable metadata. The text is immutable (→ "Nuova
// versione"); code and kind cannot change.
function EditDefinitionModal({
  definition,
  onClose,
  onSaved,
  notify,
}: {
  definition: ConsentDefinition;
  onClose: () => void;
  onSaved: () => void;
  notify: Notify;
}) {
  const [legalBasis, setLegalBasis] = useState(definition.legal_basis);
  const [blocking, setBlocking] = useState(definition.blocking);
  const [active, setActive] = useState(definition.active);
  const [contexts, setContexts] = useState(definition.contexts.join(", "));
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await updateDefinition(definition.code, {
        legal_basis: legalBasis,
        blocking,
        active,
        contexts: contexts.split(",").map((c) => c.trim()).filter(Boolean),
      });
      notify("success", `Definizione "${definition.code}" aggiornata.`);
      onSaved();
    } catch (err) {
      const msg = errorMessage(err);
      if (!isSessionExpired(msg)) notify("error", `Modifica fallita: ${msg}`);
      setBusy(false);
    }
  };

  return (
    <Modal title={`Modifica — ${definition.code}`} onClose={onClose}>
      <form className="form-grid" onSubmit={(e) => void submit(e)}>
        <p className="field__hint">
          Codice e tipo ({definition.kind}) non sono modificabili. Il testo è immutabile:
          per cambiarlo pubblica una nuova versione.
        </p>
        <div className="field">
          <label className="field__label" htmlFor="edit-legal">Base giuridica</label>
          <select
            id="edit-legal"
            className="select"
            value={legalBasis}
            onChange={(e) => setLegalBasis(e.target.value)}
            required
          >
            <option value="6.1.a">6.1.a — consenso (marketing)</option>
            <option value="6.1.b">6.1.b — misure precontrattuali</option>
            <option value="6.1.c">6.1.c — obbligo di legge</option>
          </select>
        </div>
        <div className="field">
          <label className="field__label" htmlFor="edit-contexts">Contesti</label>
          <input
            id="edit-contexts"
            className="input"
            value={contexts}
            onChange={(e) => setContexts(e.target.value)}
            placeholder="es. funnel, sso_login"
          />
          <span className="field__hint">Valori separati da virgola.</span>
        </div>
        {definition.kind === "acknowledgment" ? (
          <label className="check">
            <input type="checkbox" checked={blocking} onChange={(e) => setBlocking(e.target.checked)} />
            Bloccante (senza risposta l&apos;utente non procede)
          </label>
        ) : (
          <p className="field__hint">Un consenso (opt-in) non può essere bloccante.</p>
        )}
        <label className="check">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Attiva (se disattivata non viene più mostrata né raccolta)
        </label>
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={busy}>
            Annulla
          </button>
          <button type="submit" className="btn" disabled={busy}>
            {busy ? "Salvataggio…" : "Salva modifiche"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// Delete a definition. The API hard-deletes if it was never consented,
// otherwise soft-deletes (deactivates) to preserve the audit trail.
function DeleteDefinitionModal({
  definition,
  onClose,
  onDone,
  notify,
}: {
  definition: ConsentDefinition;
  onClose: () => void;
  onDone: () => void;
  notify: Notify;
}) {
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    setBusy(true);
    try {
      const res = await deleteDefinition(definition.code);
      if (res.deleted) {
        notify("success", `Definizione "${definition.code}" eliminata.`);
      } else {
        notify(
          "success",
          `Definizione "${definition.code}" disattivata (ha già consensi registrati: lo storico è preservato).`,
        );
      }
      onDone();
    } catch (err) {
      const msg = errorMessage(err);
      if (!isSessionExpired(msg)) notify("error", `Eliminazione fallita: ${msg}`);
      setBusy(false);
    }
  };

  return (
    <Modal title={`Elimina — ${definition.code}`} onClose={onClose}>
      <p>
        Vuoi eliminare la definizione <span className="mono">{definition.code}</span>?
      </p>
      <p className="field__hint">
        Se non ha mai raccolto consensi viene eliminata definitivamente. Se esistono
        consensi registrati non può essere cancellata (prova legale): viene invece
        <strong> disattivata</strong> e lo storico resta intatto. Puoi riattivarla da
        &ldquo;Modifica&rdquo;.
      </p>
      <div className="modal__actions">
        <button type="button" className="btn btn--ghost" onClick={onClose} disabled={busy}>
          Annulla
        </button>
        <button type="button" className="btn btn--danger" onClick={() => void confirm()} disabled={busy}>
          {busy ? "Eliminazione…" : "Elimina"}
        </button>
      </div>
    </Modal>
  );
}

// Full version history of a definition (newest first).
function VersionHistory({ def }: { def: ConsentDefinition }) {
  const versions = [...(def.versions || [])].sort((a, b) => b.version - a.version);
  const latest = versions[0]?.version;
  if (versions.length === 0) return <p className="field__hint">Nessuna versione.</p>;
  return (
    <div className="version-history">
      <div className="field__hint">Storico versioni ({versions.length})</div>
      <ol className="version-list">
        {versions.map((v) => (
          <li key={v.version} className="version-item">
            <div className="version-item__head">
              <strong>v{v.version}</strong>
              {v.version === latest ? <span className="badge badge--success">corrente</span> : null}
              <span className="field__hint">dal {formatDate(v.valid_from)}</span>
              {v.requires_reconsent ? <span className="badge badge--warn">richiede ri-consenso</span> : null}
            </div>
            <blockquote className="version-item__body">{v.body}</blockquote>
            <dl className="version-meta">
              <dt>Hash testo (SHA-256)</dt>
              <dd>
                <code className="mono" title={v.body_sha256}>
                  {v.body_sha256 ? `${v.body_sha256.slice(0, 16)}…` : "—"}
                </code>
                {v.body_sha256 ? (
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => void navigator.clipboard?.writeText(v.body_sha256)}
                    title="Copia hash"
                    aria-label={`Copia hash della versione ${v.version}`}
                  >
                    Copia
                  </button>
                ) : null}
              </dd>
              <dt>ID versione</dt>
              <dd><code className="mono">{v.id || "—"}</code></dd>
              <dt>Ri-consenso richiesto</dt>
              <dd>{v.requires_reconsent ? "Sì" : "No"}</dd>
              <dt>In vigore dal</dt>
              <dd>{formatDate(v.valid_from)}</dd>
            </dl>
          </li>
        ))}
      </ol>
    </div>
  );
}

type SortKey = "code" | "kind" | "legal_basis" | "blocking" | "contexts" | "version";
const DEFS_PAGE_SIZE = 10;

function DefinitionsSection({ notify }: { notify: Notify }) {
  const [defs, setDefs] = useState<ConsentDefinition[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showNewDefinition, setShowNewDefinition] = useState(false);
  const [versionTarget, setVersionTarget] = useState<ConsentDefinition | null>(null);
  const [editTarget, setEditTarget] = useState<ConsentDefinition | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ConsentDefinition | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>("code");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const load = () => {
    setError(null);
    fetchDefinitions()
      .then(setDefs)
      .catch((err) => {
        const msg = errorMessage(err);
        if (!isSessionExpired(msg)) setError(msg);
      });
  };

  useEffect(load, []);

  const sorted = useMemo(() => {
    if (!defs) return [];
    const val = (d: ConsentDefinition): string | number => {
      switch (sortKey) {
        case "kind": return d.kind;
        case "legal_basis": return d.legal_basis;
        case "blocking": return d.blocking ? 1 : 0;
        case "contexts": return d.contexts.join(", ");
        case "version": return currentVersion(d)?.version ?? 0;
        default: return d.code;
      }
    };
    return [...defs].sort((a, b) => {
      const va = val(a);
      const vb = val(b);
      const c =
        typeof va === "number" && typeof vb === "number"
          ? va - vb
          : String(va).localeCompare(String(vb));
      return sortDir === "asc" ? c : -c;
    });
  }, [defs, sortKey, sortDir]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / DEFS_PAGE_SIZE));
  const clampedPage = Math.min(page, pageCount - 1);
  const pageRows = sorted.slice(clampedPage * DEFS_PAGE_SIZE, clampedPage * DEFS_PAGE_SIZE + DEFS_PAGE_SIZE);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(0);
  };
  const sortInd = (key: SortKey) => (sortKey === key ? (sortDir === "asc" ? " ▲" : " ▼") : "");
  const toggleExpand = (code: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  return (
    <div className="card">
      <div className="card__title">
        <h2>Definizioni</h2>
        {defs ? <span className="badge">{defs.length}</span> : null}
        <button
          type="button"
          className="btn btn--sm"
          style={{ marginLeft: "auto" }}
          onClick={() => setShowNewDefinition(true)}
        >
          Nuova definizione
        </button>
      </div>

      {error ? (
        <div className="alert alert--error" role="alert">
          Impossibile caricare le definizioni: {error}{" "}
          <button type="button" className="btn btn--ghost btn--sm" onClick={load}>
            Riprova
          </button>
        </div>
      ) : defs === null ? (
        <TableSkeleton />
      ) : defs.length === 0 ? (
        <div className="empty-state">
          <p>Nessuna definizione di consenso ancora.</p>
          <p>Crea la prima con &ldquo;Nuova definizione&rdquo;.</p>
        </div>
      ) : (
        <>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 32 }}>
                    <span className="sr-only">Espandi</span>
                  </th>
                  <th>
                    <button type="button" className="th-sort" onClick={() => toggleSort("code")}>
                      Codice{sortInd("code")}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="th-sort" onClick={() => toggleSort("kind")}>
                      Tipo{sortInd("kind")}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="th-sort" onClick={() => toggleSort("legal_basis")}>
                      Base giuridica{sortInd("legal_basis")}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="th-sort" onClick={() => toggleSort("blocking")}>
                      Bloccante{sortInd("blocking")}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="th-sort" onClick={() => toggleSort("contexts")}>
                      Contesti{sortInd("contexts")}
                    </button>
                  </th>
                  <th>
                    <button type="button" className="th-sort" onClick={() => toggleSort("version")}>
                      Versione{sortInd("version")}
                    </button>
                  </th>
                  <th>
                    <span className="sr-only">Azioni</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((def) => {
                  const ver = currentVersion(def);
                  const isOpen = expanded.has(def.code);
                  return (
                    <Fragment key={def.code}>
                      <tr>
                        <td>
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            aria-expanded={isOpen}
                            aria-label={isOpen ? "Comprimi versioni" : "Espandi versioni"}
                            onClick={() => toggleExpand(def.code)}
                          >
                            {isOpen ? "▾" : "▸"}
                          </button>
                        </td>
                        <td>
                          <span className="mono">{def.code}</span>{" "}
                          {!def.active ? <span className="badge badge--neutral">inattiva</span> : null}
                        </td>
                        <td>
                          <span className={kindBadgeClass(def.kind)}>{def.kind}</span>
                        </td>
                        <td>{def.legal_basis}</td>
                        <td>{def.blocking ? <span className="badge badge--warn">Sì</span> : "No"}</td>
                        <td>{def.contexts.length ? def.contexts.join(", ") : "—"}</td>
                        <td>
                          {ver ? (
                            <>
                              v{ver.version}
                              <span className="field__hint"> dal {formatDate(ver.valid_from)}</span>
                            </>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>
                          <div className="row-actions">
                            <button
                              type="button"
                              className="icon-btn"
                              title="Nuova versione"
                              aria-label={`Nuova versione di ${def.code}`}
                              onClick={() => setVersionTarget(def)}
                            >
                              <IconNewVersion />
                            </button>
                            <button
                              type="button"
                              className="icon-btn"
                              title="Modifica"
                              aria-label={`Modifica ${def.code}`}
                              onClick={() => setEditTarget(def)}
                            >
                              <IconEdit />
                            </button>
                            <button
                              type="button"
                              className="icon-btn icon-btn--danger"
                              title="Elimina"
                              aria-label={`Elimina ${def.code}`}
                              onClick={() => setDeleteTarget(def)}
                            >
                              <IconDelete />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isOpen ? (
                        <tr className="detail-row">
                          <td colSpan={8}>
                            <VersionHistory def={def} />
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pageCount > 1 ? (
            <div className="pager">
              <button type="button" className="btn btn--ghost btn--sm" disabled={clampedPage === 0} onClick={() => setPage(clampedPage - 1)}>
                ← Prec
              </button>
              <span className="field__hint">
                Pagina {clampedPage + 1} di {pageCount}
              </span>
              <button type="button" className="btn btn--ghost btn--sm" disabled={clampedPage >= pageCount - 1} onClick={() => setPage(clampedPage + 1)}>
                Succ →
              </button>
            </div>
          ) : null}
        </>
      )}

      {showNewDefinition ? (
        <NewDefinitionModal
          onClose={() => setShowNewDefinition(false)}
          onSaved={() => {
            setShowNewDefinition(false);
            load();
          }}
          notify={notify}
        />
      ) : null}
      {versionTarget ? (
        <NewVersionModal
          definition={versionTarget}
          onClose={() => setVersionTarget(null)}
          onSaved={() => {
            setVersionTarget(null);
            load();
          }}
          notify={notify}
        />
      ) : null}
      {editTarget ? (
        <EditDefinitionModal
          definition={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => {
            setEditTarget(null);
            load();
          }}
          notify={notify}
        />
      ) : null}
      {deleteTarget ? (
        <DeleteDefinitionModal
          definition={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDone={() => {
            setDeleteTarget(null);
            load();
          }}
          notify={notify}
        />
      ) : null}
    </div>
  );
}

// ----- Soggetti ------------------------------------------------------------

function RevokeModal({
  consent,
  partyId,
  busy,
  onCancel,
  onConfirm,
}: {
  consent: PartyConsent;
  partyId: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal title="Revoca consenso" onClose={onCancel}>
      <p>
        Stai per revocare il consenso <strong className="mono">{consent.definition_code}</strong>{" "}
        (v{consent.version}) per il soggetto <span className="mono">{partyId}</span>.
      </p>
      <div className="alert alert--error">
        L&apos;operazione è <strong>irreversibile</strong>: viene registrato un evento{" "}
        <code>revoked</code> nell&apos;event log e non può essere annullata da qui. Solo il
        soggetto potrà eventualmente prestare di nuovo il consenso.
      </div>
      <div className="modal__actions">
        <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={busy}>
          Annulla
        </button>
        <button type="button" className="btn btn--danger" onClick={onConfirm} disabled={busy}>
          {busy ? "Revoca…" : "Revoca consenso"}
        </button>
      </div>
    </Modal>
  );
}

function EventLog({ events, error }: { events: ConsentEvent[] | null; error: string | null }) {
  if (error) {
    return (
      <div className="alert alert--error" role="alert">
        Impossibile caricare l&apos;event log: {error}
      </div>
    );
  }
  if (events === null) return <TableSkeleton />;
  if (events.length === 0) {
    return <div className="empty-state">Nessun evento registrato per questo soggetto.</div>;
  }
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Data</th>
            <th>Definizione</th>
            <th>Azione</th>
            <th>Versione</th>
            <th>Canale</th>
            <th>Proof</th>
          </tr>
        </thead>
        <tbody>
          {events.map((ev) => {
            const proof =
              ev.proof === null || ev.proof === undefined ? null : JSON.stringify(ev.proof);
            return (
              <tr key={ev.id}>
                <td>{formatDate(ev.occurred_at)}</td>
                <td>
                  <span className="mono">{ev.definition_code}</span>
                </td>
                <td>
                  <span className={actionBadgeClass(ev.action)}>{ev.action}</span>
                </td>
                <td>v{ev.version}</td>
                <td>{ev.channel}</td>
                <td>
                  {proof ? (
                    <span className="mono" title={proof}>
                      {proof.length > 48 ? `${proof.slice(0, 48)}…` : proof}
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SubjectsSection({ notify }: { notify: Notify }) {
  const [type, setType] = useState<PartyIdentifierType>("email");
  const [identifier, setIdentifier] = useState("");
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PartyLookupResult | null>(null);
  const [lastQuery, setLastQuery] = useState<{ identifier: string; type: PartyIdentifierType } | null>(null);

  const [revokeTarget, setRevokeTarget] = useState<PartyConsent | null>(null);
  const [revokeBusy, setRevokeBusy] = useState(false);

  const [eventsOpen, setEventsOpen] = useState(false);
  const [events, setEvents] = useState<ConsentEvent[] | null>(null);
  const [eventsError, setEventsError] = useState<string | null>(null);

  const resetResult = () => {
    setResult(null);
    setEvents(null);
    setEventsError(null);
    setEventsOpen(false);
  };

  const search = async (e: FormEvent) => {
    e.preventDefault();
    const value = identifier.trim();
    if (!value) return;
    setLoading(true);
    setError(null);
    setSearched(true);
    resetResult();
    try {
      setResult(await lookupParty(value, type));
      setLastQuery({ identifier: value, type });
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        // Nessun soggetto: lo stato "searched && !result" mostra l'empty state.
      } else {
        const msg = errorMessage(err);
        if (!isSessionExpired(msg)) setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadEvents = async (partyId: string) => {
    setEventsError(null);
    try {
      setEvents(await fetchPartyEvents(partyId));
    } catch (err) {
      const msg = errorMessage(err);
      if (!isSessionExpired(msg)) setEventsError(msg);
    }
  };

  const toggleEvents = () => {
    if (!result) return;
    const open = !eventsOpen;
    setEventsOpen(open);
    if (open && events === null) void loadEvents(result.party.id);
  };

  const confirmRevoke = async () => {
    if (!revokeTarget || !result || !lastQuery) return;
    setRevokeBusy(true);
    try {
      await revokeConsent(result.party.id, revokeTarget.definition_code);
      notify("success", `Consenso "${revokeTarget.definition_code}" revocato.`);
      setRevokeTarget(null);
      // Refresh: stato consensi + event log (se aperto).
      setResult(await lookupParty(lastQuery.identifier, lastQuery.type));
      setEvents(null);
      if (eventsOpen) void loadEvents(result.party.id);
    } catch (err) {
      const msg = errorMessage(err);
      if (!isSessionExpired(msg)) notify("error", `Revoca fallita: ${msg}`);
    } finally {
      setRevokeBusy(false);
    }
  };

  return (
    <>
      <div className="card">
        <div className="card__title">
          <h2>Ricerca soggetto</h2>
        </div>
        <form className="lookup-form" onSubmit={(e) => void search(e)}>
          <div className="field">
            <label className="field__label" htmlFor="lookup-type">Tipo identificatore</label>
            <select
              id="lookup-type"
              className="select"
              value={type}
              onChange={(e) => setType(e.target.value as PartyIdentifierType)}
            >
              {IDENTIFIER_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="field__label" htmlFor="lookup-value">Identificatore</label>
            <input
              id="lookup-value"
              className="input"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="es. mario.rossi@example.com"
              required
            />
          </div>
          <button type="submit" className="btn" disabled={loading}>
            {loading ? "Ricerca…" : "Cerca"}
          </button>
        </form>
      </div>

      {error ? (
        <div className="card">
          <div className="alert alert--error" role="alert">
            Ricerca fallita: {error}
          </div>
        </div>
      ) : loading ? (
        <div className="card">
          <TableSkeleton />
        </div>
      ) : result ? (
        <>
          <div className="card">
            <div className="card__title">
              <h2>Soggetto</h2>
              <span
                className={
                  result.party.status.toLowerCase() === "active"
                    ? "badge badge--success"
                    : "badge badge--neutral"
                }
              >
                {result.party.status}
              </span>
            </div>
            <div className="table-wrap">
              <table className="table">
                <tbody>
                  <tr>
                    <th scope="row">ID</th>
                    <td>
                      <span className="mono">{result.party.id}</span>
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">Tipo</th>
                    <td>
                      {IDENTIFIER_TYPES.find((t) => t.value === result.party.type)?.label ??
                        result.party.type}
                    </td>
                  </tr>
                  <tr>
                    <th scope="row">Creato il</th>
                    <td>{formatDate(result.party.created_at)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <div className="card__title">
              <h2>Consensi correnti</h2>
              <span className="badge">{result.consents.length}</span>
            </div>
            {result.consents.length === 0 ? (
              <div className="empty-state">Nessun consenso registrato per questo soggetto.</div>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Definizione</th>
                      <th>Stato</th>
                      <th>Versione</th>
                      <th>Data</th>
                      <th>Canale</th>
                      <th>
                        <span className="sr-only">Azioni</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.consents.map((c) => (
                      <tr key={`${c.definition_code}-${c.version}`}>
                        <td>
                          <span className="mono">{c.definition_code}</span>
                        </td>
                        <td>
                          <span className={actionBadgeClass(c.action)}>{c.action}</span>
                        </td>
                        <td>v{c.version}</td>
                        <td>{formatDate(c.occurred_at)}</td>
                        <td>{c.channel}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn--ghost btn--sm"
                            onClick={() => setRevokeTarget(c)}
                            disabled={c.action.toLowerCase().includes("revok")}
                          >
                            Revoca
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="card">
            <div className="card__title">
              <h2>Event log</h2>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                style={{ marginLeft: "auto" }}
                aria-expanded={eventsOpen}
                onClick={toggleEvents}
              >
                {eventsOpen ? "Nascondi" : "Mostra"}
              </button>
            </div>
            {eventsOpen ? (
              <EventLog events={events} error={eventsError} />
            ) : (
              <p className="field__hint" style={{ margin: 0 }}>
                Cronologia completa degli eventi di consenso del soggetto (grant, revoche,
                nuove versioni), con proof di raccolta.
              </p>
            )}
          </div>
        </>
      ) : searched ? (
        <div className="card">
          <div className="empty-state">
            <p>Nessun soggetto trovato per l&apos;identificatore indicato.</p>
            <p>Verifica il tipo di identificatore selezionato e riprova.</p>
          </div>
        </div>
      ) : null}

      {revokeTarget && result ? (
        <RevokeModal
          consent={revokeTarget}
          partyId={result.party.id}
          busy={revokeBusy}
          onCancel={() => setRevokeTarget(null)}
          onConfirm={() => void confirmRevoke()}
        />
      ) : null}
    </>
  );
}

// ----- Pagina --------------------------------------------------------------

export function ConsentsPage() {
  const [tab, setTab] = useState<"definitions" | "subjects">("definitions");
  const [toast, setToast] = useState<{ kind: ToastKind; text: string } | null>(null);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4500);
    return () => clearTimeout(timer);
  }, [toast]);

  const notify: Notify = (kind, text) => setToast({ kind, text });

  return (
    <>
      <div className="page-head">
        <h1>Gestione consensi</h1>
        <p>
          Amministra le definizioni di consenso e presa visione, pubblica nuove versioni dei
          testi e verifica lo stato dei consensi dei singoli soggetti.
        </p>
      </div>

      <div className="tabs" role="tablist" aria-label="Sezioni gestione consensi">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "definitions"}
          className={tab === "definitions" ? "tab tab--active" : "tab"}
          onClick={() => setTab("definitions")}
        >
          Definizioni
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "subjects"}
          className={tab === "subjects" ? "tab tab--active" : "tab"}
          onClick={() => setTab("subjects")}
        >
          Soggetti
        </button>
      </div>

      {tab === "definitions" ? (
        <DefinitionsSection notify={notify} />
      ) : (
        <SubjectsSection notify={notify} />
      )}

      {toast ? (
        <div className={`toast toast--${toast.kind}`} role="status">
          {toast.text}
        </div>
      ) : null}
    </>
  );
}
